import React, { useState } from 'react';
import { IAgentTask, IMarketResearchInsight } from '../../interfaces/domain';
import Button, { EButtonColor, EButtonSize } from '../../components/UI/Button/Button';
import MarkdownRenderer from '../../components/UI/MarkdownRenderer/MarkdownRenderer';
import { parseAgentTaskDetails } from '../../shared/agent-task-utils';

import classes from './AgentTaskCard.module.css';

const WorkbookContextView: React.FC<{ context: string }> = ({ context }) => {
  const [showFull, setShowFull] = useState(false);
  
  if (context.length <= 1000) {
    return (
      <pre className={classes.pre}>{context}</pre>
    );
  }
  
  return (
    <div>
      <pre className={classes.pre} style={{ maxHeight: showFull ? 'none' : '200px' }}>
        {showFull ? context : `${context.substring(0, 1000)}\n\n... [Tekstiä lyhennetty, yhteensä ${context.length} merkkiä]`}
      </pre>
      <button 
        type="button" 
        onClick={(e) => { e.stopPropagation(); setShowFull(!showFull); }} 
        style={{
          marginTop: '0.5rem',
          background: 'none',
          border: 'none',
          color: '#2563eb',
          cursor: 'pointer',
          fontSize: '0.875rem',
          fontWeight: 'bold',
          padding: 0,
          textDecoration: 'underline'
        }}
      >
        {showFull ? 'Näytä vähemmän ▲' : 'Näytä koko teksti ▼'}
      </button>
    </div>
  );
};

/** Mapping of taskType to display labels */
const TASK_TYPE_LABELS: Record<string, { icon: string; label: string }> = {
  RESEARCH_GATEKEEPER: { icon: '⚖️', label: 'Portinvartija' },
  RESEARCH_FACTORY: { icon: '🏭', label: 'Tehdastyöläinen' },
  RESEARCH_SCAFFOLD: { icon: '🔧', label: 'Scaffolder' },
  RESEARCH_FOCUS: { icon: '🔍', label: 'Fokustutkimus' },
  COMPLIANCE_LEGAL: { icon: '⚖️', label: 'Lainmukaisuus' },
  COMPETITORS_PRICING: { icon: '💰', label: 'Kilpailijat & hinnoittelu' },
  SEO_KEYWORDS: { icon: '🔎', label: 'SEO & avainsanat' },
  SUPPLIERS_B2B: { icon: '🤝', label: 'B2B-kontaktointi' },
  MARKET_TAM_SAM: { icon: '📊', label: 'TAM/SAM-analyysi' },
};

const STATUS_COLORS: Record<string, string> = {
  PENDING: '#94a3b8',
  PROCESSING: '#f59e0b',
  POLLING: '#3b82f6',
  COMPLETED: '#22c55e',
  FAILED: '#ef4444',
  FLAGGED: '#a855f7',
};

const MODEL_TYPE_LABELS: Record<string, string> = {
  DEEP_RESEARCH: '🧠 Deep Research',
  VERTEX_AI: '⚡ Gemini Flash',
};

/** Statuses that indicate the task is actively waiting for Gemini */
const POLLING_STATUSES = new Set(['POLLING', 'PROCESSING']);

interface IProps {
  agentTask: IAgentTask;
  insight?: IMarketResearchInsight;
  onDelete: (id: string) => void;
  onRefresh?: (id: string) => Promise<void>;
}

const AgentTaskCard: React.FC<IProps> = ({ agentTask, insight, onDelete, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState<string | null>(null);

  const typeInfo = TASK_TYPE_LABELS[agentTask.taskType] || { icon: '📋', label: agentTask.taskType };
  const statusColor = STATUS_COLORS[agentTask.status] || '#94a3b8';
  const modelLabel = MODEL_TYPE_LABELS[agentTask.modelType || 'DEEP_RESEARCH'] || agentTask.modelType;
  const isPolling = POLLING_STATUSES.has(agentTask.status);

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs.toFixed(0)}s`;
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Haluatko varmasti poistaa tämän tutkimustehtävän?')) {
      onDelete(agentTask.id);
    }
  };

  const handleRefreshClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onRefresh) return;
    setRefreshing(true);
    setRefreshResult(null);
    try {
      await onRefresh(agentTask.id);
      setRefreshResult('✓ Tila päivitetty');
    } catch (err: any) {
      setRefreshResult('⚠ Virhe: ' + (err?.message || 'Tuntematon virhe'));
    } finally {
      setRefreshing(false);
      setTimeout(() => setRefreshResult(null), 4000);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className={classes.card}>
      <div className={classes.header} onClick={() => setExpanded(!expanded)}>
        <div className={classes.headerLeft}>
          <span className={classes.icon}>{typeInfo.icon}</span>
          <span className={classes.label}>{typeInfo.label}</span>
          <span className={classes.statusBadge} style={{ backgroundColor: statusColor }}>
            {agentTask.status === 'POLLING' ? '⏳ POLLING' : agentTask.status}
          </span>
          <span className={classes.modelBadge}>{modelLabel}</span>
        </div>
        <div className={classes.headerRight}>
          {agentTask.duration != null && (
            <span className={classes.metric}>⏱ {formatDuration(agentTask.duration)}</span>
          )}
          {agentTask.totalTokens != null && agentTask.totalTokens > 0 && (
            <span className={classes.metric}>🪙 {agentTask.totalTokens}</span>
          )}
          {/* Manual refresh button — shown for POLLING / PROCESSING tasks */}
          {isPolling && onRefresh && (
            <button
              onClick={handleRefreshClick}
              disabled={refreshing}
              title="Tarkista Gemini-interaktion tila nyt"
              style={{
                background: refreshing ? '#e2e8f0' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                fontSize: '0.8rem',
                fontWeight: 600,
                padding: '0.3rem 0.65rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem',
                transition: 'all 0.2s',
                boxShadow: refreshing ? 'none' : '0 1px 4px rgba(59,130,246,0.3)',
              }}
            >
              <span style={{
                display: 'inline-block',
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
              }}>🔄</span>
              {refreshing ? 'Tarkistetaan...' : 'Tarkista tila'}
            </button>
          )}
          {refreshResult && (
            <span style={{
              fontSize: '0.75rem',
              color: refreshResult.startsWith('✓') ? '#16a34a' : '#dc2626',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}>
              {refreshResult}
            </span>
          )}
          <span className={classes.expandIcon}>{expanded ? '▼' : '▶'}</span>
          <Button
            size={EButtonSize.SMALL}
            color={EButtonColor.DANGER}
            onClick={handleDeleteClick}
          >
            ✕
          </Button>
        </div>
      </div>

      {expanded && (
        <div className={classes.body}>
          <div className={classes.meta}>
            <div className={classes.metaItem}>
              <strong>Tyyppi:</strong> {agentTask.taskType}
            </div>
            <div className={classes.metaItem}>
              <strong>Malli:</strong> {agentTask.modelType || 'DEEP_RESEARCH'}
            </div>
            {agentTask.interactionId && (
              <div className={classes.metaItem}>
                <strong>Interaction ID:</strong> <code>{agentTask.interactionId}</code>
              </div>
            )}
            {agentTask.totalTokens != null && agentTask.totalTokens > 0 && (
              <div className={classes.metaItem}>
                <strong>Tokenit:</strong> Input: {agentTask.promptTokens?.toLocaleString('fi-FI') || 0} / Output: {agentTask.candidateTokens?.toLocaleString('fi-FI') || 0} (Yhteensä: {agentTask.totalTokens.toLocaleString('fi-FI')})
              </div>
            )}
            {agentTask.created && (
              <div className={classes.metaItem}>
                <strong>Luotu:</strong> {new Date(agentTask.created).toLocaleString('fi-FI')}
              </div>
            )}
          </div>

          {agentTask.status === 'POLLING' && (
            <div style={{
              margin: '0.5rem 0',
              padding: '0.75rem 1rem',
              background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
              borderLeft: '4px solid #3b82f6',
              borderRadius: '0 6px 6px 0',
              fontSize: '0.875rem',
              color: '#1d4ed8',
            }}>
              <strong>⏳ Gemini Deep Research käynnissä</strong> — Tutkimus on lähetetty Googlen pilveen. Cron-ajuri tarkistaa tilan 5 minuutin välein tai voit tarkistaa manuaalisesti yllä olevalla painikkeella.
            </div>
          )}

          {(() => {
            const parsedDetails = parseAgentTaskDetails(agentTask.details);
            const hasInputs = parsedDetails.customQuery || parsedDetails.workbookContext || parsedDetails.plainText;
            return (
              <>
                {hasInputs && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.25rem' }}>
                    {parsedDetails.customQuery && (
                      <div className={classes.section}>
                        <h4>Mukautettu kysymys / AI-ohjeet</h4>
                        <pre className={classes.pre}>{parsedDetails.customQuery}</pre>
                      </div>
                    )}
                    {parsedDetails.workbookContext && (
                      <div className={classes.section}>
                        <h4>Lähdeaineisto ({parsedDetails.workbookContext.length} merkkiä)</h4>
                        <WorkbookContextView context={parsedDetails.workbookContext} />
                      </div>
                    )}
                    {parsedDetails.plainText && (
                      <div className={classes.section}>
                        <h4>Tehtävän tiedot</h4>
                        <pre className={classes.pre}>{parsedDetails.plainText}</pre>
                      </div>
                    )}
                  </div>
                )}
                {agentTask.status === 'FAILED' && (
                  <div style={{
                    margin: '0.5rem 0 1.25rem 0',
                    padding: '0.75rem 1rem',
                    background: '#fee2e2',
                    borderLeft: '4px solid #ef4444',
                    borderRadius: '0 6px 6px 0',
                    fontSize: '0.875rem',
                    color: '#dc2626',
                  }}>
                    <strong style={{ display: 'block', textTransform: 'uppercase', fontSize: '0.75rem', color: '#ef4444', marginBottom: '0.25rem' }}>Virheilmoitus</strong>
                    <span style={{ fontWeight: 'bold', whiteSpace: 'pre-wrap' }}>
                      {parsedDetails.errorMessage || 'Tehtäväajon suoritus epäonnistui. Lisätietoja ei ole saatavilla.'}
                    </span>
                  </div>
                )}
              </>
            );
          })()}

          {insight && insight.responseText && (
            <div className={classes.section}>
              <div className={classes.sectionHeader}>
                <h4>Tutkimustulos</h4>
                <Button
                  size={EButtonSize.SMALL}
                  onClick={() => copyToClipboard(insight.responseText)}
                >
                  📋 Kopioi
                </Button>
              </div>
              <div className={classes.resultContent}>
                <MarkdownRenderer
                  text={insight.responseText}
                  extractFinalReport={agentTask.modelType === 'DEEP_RESEARCH'}
                />

                {/* Grounding sources / citations */}
                {insight.sourcesJson && (
                  <div style={{ marginTop: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
                    <h5 style={{ margin: '0 0 0.5rem 0', color: '#475569', fontSize: '0.85rem', fontWeight: 600 }}>
                      🔍 Käytetyt verkkolähteet & viittaukset:
                    </h5>
                    <ul style={{ paddingLeft: '1.2rem', margin: 0, fontSize: '0.8rem', color: '#1e293b' }}>
                      {(() => {
                        try {
                          const parsed = JSON.parse(insight.sourcesJson) as Array<{ title?: string; url?: string }>;
                          return parsed.map((s, idx) => (
                            <li key={idx} style={{ marginBottom: '0.25rem' }}>
                              {s.url ? (
                                <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}>
                                  {s.title || s.url} 🔗
                                </a>
                              ) : (
                                <span>{s.title}</span>
                              )}
                            </li>
                          ));
                        } catch (e) {
                          return null;
                        }
                      })()}
                    </ul>
                  </div>
                )}

                {insight.searchQueriesJson && (
                  <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: '#64748b', borderTop: insight.sourcesJson ? 'none' : '1px solid #e2e8f0', paddingTop: insight.sourcesJson ? 0 : '0.75rem' }}>
                    <strong>LLM-hakuavainsanat:</strong>{' '}
                    {(() => {
                      try {
                        const queries = JSON.parse(insight.searchQueriesJson) as string[];
                        return queries.join(', ');
                      } catch (e) {
                        return null;
                      }
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}

          {!insight && agentTask.status === 'COMPLETED' && (
            <div className={classes.section}>
              <p className={classes.noResult}>Tutkimustulos ei ole saatavilla.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentTaskCard;
